import React, { useState } from 'react';
import { Formik } from 'formik';
import { Card, Button, Modal, Menu, message } from 'antd';
import { FormikForm } from 'client/ui/form/FormikForm';
import { useDispatch } from 'react-redux';
import { AuthActions, useCurrentCitizen, useCurrentUser } from '../AuthModule';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { AuthApi } from '../AuthApi';
import { TextInputItem } from 'client/ui/form/input/TextInputItem';
import {
  ICreateAccessTokenDto,
  createAccessTokenValidator
} from 'common/dto/AuthDto';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { LoaderSpin } from 'client/ui/loader/LoaderSpin';
import {
  EditOutlined,
  LockOutlined,
  MailOutlined,
  UserOutlined
} from '@ant-design/icons';
import { MenuItemProps } from 'antd/lib/menu/MenuItem';
import SubMenu from 'antd/lib/menu/SubMenu';
import { LogoutButton } from '../logout/LogoutButton';
import { getNetworkErrorMessage } from 'client/core/network/errors/getNetworkErrorMessage';
import { Link, useHistory } from 'react-router-dom';
import { resolveApiPath } from 'client/core/appConfig';
import {
  useClientConfig,
  useHasFeature,
  useModuleConfigOptions,
  useSecurityStrategyConfig
} from 'client/components/schema/config/ClientConfigModule';
import { RoutePaths } from 'common/routes/RoutePaths';
import { useTranslation } from 'react-i18next';
import { UserLogic } from 'common/schema/UserLogic';

interface Props extends MenuItemProps {}

/**
 * Menu che mostra le informazioni sul cittadino-utente loggato e le
 * azioni disponibili, quali il logout.
 */
export function UserActionsMenu(props: Props) {
  const { ...otherProps } = props;
  const user = useCurrentUser();
  const citizen = useCurrentCitizen();
  const history = useHistory();
  const { t } = useTranslation();

  // Ottiene informazioni sul login SPID (SAML)
  const samlConfig = useSecurityStrategyConfig('saml');
  // Ottiene informazioni sul login password-based
  const hasRegistration = useHasFeature('citizen.registration');

  // Se l'utente-cittadino è loggato, mostriamo il suo nome e il tasto di
  // logout.
  if (citizen) {
    return (
      <SubMenu
        rootPrefixCls="main-menu-submenu"
        popupOffset={[0, 1]}
        key="user"
        icon={<UserOutlined />}
        title={`${citizen.fullName}`}
        style={{ float: 'right' }}
        {...otherProps}
      >
        {UserLogic.canEditProfile(user) && (
          <Menu.Item key="user/profile">
            <Link to="/utente/profilo">
              <EditOutlined /> Profilo
            </Link>
          </Menu.Item>
        )}
        <Menu.Item key="user/logout">
          <LogoutButton />
        </Menu.Item>
      </SubMenu>
    );
  }

  return (
    <>
      {samlConfig && (
        <Menu.Item key="login-saml" style={{ float: 'right' }} {...otherProps}>
          <a href={resolveApiPath('/auth/iam/sso')}>
            {samlConfig.loginButtonText ?? 'Accedi con SPID'}
          </a>
        </Menu.Item>
      )}
      {hasRegistration && (
        <Menu.Item key="login" style={{ float: 'right' }} {...otherProps}>
          <Link
            to={{
              pathname: RoutePaths['/utente/login'],
              state: { from: history.location.pathname }
            }}
          >
            {t('menu.user-actions.login', 'Accedi')}
          </Link>
        </Menu.Item>
      )}
    </>
  );
}
